import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { TutorialStep } from 'src/replicant/buruburu/tutorial/tutorial.ruleset';
import { ImageCoverView, ImageScaleView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { waitForAssets } from 'src/lib/utils';
import { petTutorialAssets } from 'src/loadingGroups';
import StateObserver from 'src/StateObserver';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = () => void;
const ui = uiConfig.buruburu.tutorial;

export default class PopupTutorialStep extends PopupBasic {
  private close: CloseHandler;
  private page: number;
  private tutorialAssetUrls: string[];
  private tutorialImage: ImageCoverView;
  private pageIndicator: LangBitmapFontTextView;
  private prevButton: ButtonScaleView;
  private nextButton: ButtonScaleView;
  private closeButton: ButtonScaleViewWithText;

  // Scale down the tutorial popups a little bit to fit inside shorter screens
  protected viewScale = 0.9;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...ui.popup,
      tracking: { feature: 'tutorial', $subFeature: 'popup-tutorial-step' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.box.addSubview(new ImageScaleView(ui.frame));
    this.box.addSubview(
      new ImageScaleView({
        ...ui.frame,
        x: ui.frame.width - 1,
        flipX: true,
      }),
    );

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.tutorialImage = new ImageCoverView({
      width: 507,
      height: 901,
      x: 69, // nice
      y: 97,
      zIndex: -1,
      superview: this.box,
    });

    this.pageIndicator = new LangBitmapFontTextView({
      ...ui.pageIndicator,
      superview: this.box,
    });

    this.prevButton = new ButtonScaleView({
      ...ui.prevButton,
      superview: this.box,
      onClick: async () => {
        sounds.sfx.play('cancel');
        this.setPage(this.page - 1);
      },
      tracking: {
        feature: 'tutorial',
        $subFeature: 'popup-tutorial-step-back',
      },
    });

    this.nextButton = new ButtonScaleView({
      ...ui.nextButton,
      superview: this.box,
      onClick: async () => {
        sounds.sfx.play('ok');
        this.setPage(this.page + 1);
      },
      tracking: {
        feature: 'tutorial',
        $subFeature: 'popup-tutorial-step-next',
      },
    });

    this.closeButton = new ButtonScaleViewWithText({
      ...ui.closeButton,
      superview: this.box,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');

        const nextPage = this.page + 1;
        if (this.tutorialAssetUrls[nextPage]) {
          this.setPage(nextPage);
          return;
        }

        return opts.close();
      },
      tracking: {
        feature: 'tutorial',
        $subFeature: 'popup-tutorial-step-close',
      },
    });
  }

  async init({ step, page }: { step: TutorialStep; page?: number }) {
    const { locale } = StateObserver.getState().ui;
    this.tutorialAssetUrls = ui.images[step][locale];
    let assetGroup = petTutorialAssets[step][locale];

    await waitForAssets(assetGroup);
    super.init({
      message: i18n(`tutorial.steps.${step}`),
    });

    this.setPage(page ?? 0);
  }

  private setPage(page: number) {
    this.page = page;
    this.tutorialImage.setImage(this.tutorialAssetUrls[page]);

    const hasPrevious = !!this.tutorialAssetUrls[page - 1];
    this.prevButton.updateOpts({
      visible: hasPrevious,
    });

    const hasNext = !!this.tutorialAssetUrls[page + 1];

    this.nextButton.updateOpts({
      visible: hasNext,
    });

    this.closeButton.localeText = () =>
      i18n(`basic.${hasNext ? 'next' : 'ok'}`);

    this.pageIndicator.localeText = () =>
      `${page + 1}/${this.tutorialAssetUrls.length}`;
  }
}
